// Demo seed for mock mode. Aligned with the backend Deal response shape
// (invoices / shipments live in side tables keyed by dealId) and the new
// invoice cents-based shape (`invoiceNumber` / `period: object` / *Cents
// numeric fields). All monetary values here are integer cents.
//
// `companyId` / `businessId` use human-readable strings rather than MongoDB
// ObjectIds. The real `/internal/company` catalog will not have entries for
// these, so DealRow falls back to rendering the companyId directly — which
// is exactly the readable name. Real backend rows resolve through the real
// catalog and look fine.

import type { Deal, Shipment } from '../../types/deal'
import type { Invoice } from '../../types/invoice'

type InvoicesByDealId = Record<string, Invoice[]>
type ShipmentsByDealId = Record<string, Shipment[]>

function summaryFor(invoices: Invoice[]): Deal['invoiceSummary'] {
  const statuses = Array.from(new Set(invoices.map((entry) => entry.status)))
  const outstandingTotalCents = invoices.reduce(
    (sum, entry) => sum + Math.max(0, entry.outstandingCents ?? 0),
    0,
  )
  const countOf = (status: Invoice['status']): number =>
    invoices.filter((entry) => entry.status === status).length
  return {
    totalInvoices: invoices.length,
    paidInvoices: countOf('Paid'),
    unpaidInvoices: countOf('Unpaid'),
    partiallyPaidInvoices: countOf('Partially Paid'),
    scheduledInvoices: countOf('Scheduled'),
    draftInvoices: countOf('Draft'),
    voidedInvoices: countOf('Voided'),
    outstandingTotalCents,
    invoiceStatuses: statuses,
  }
}

function shipmentProgressFor(shipments: Shipment[]): Deal['shipmentProgress'] {
  const isDelivered = (shipment: Shipment): boolean => {
    const lastCustomerLeg = [...shipment.legs]
      .reverse()
      .find((leg) => leg.type === 'customer')
    return lastCustomerLeg?.status === 'delivered'
  }
  const totalDevices = shipments.reduce((sum, shipment) => sum + shipment.devices.length, 0)
  const deliveredDevices = shipments
    .filter(isDelivered)
    .reduce((sum, shipment) => sum + shipment.devices.length, 0)
  return {
    totalShip: shipments.length,
    deliveredShip: shipments.filter(isDelivered).length,
    totalDevices,
    deliveredDevices,
    hasShipments: shipments.length > 0,
  }
}

const NOW_ISO = new Date('2026-01-25T00:00:00.000Z').toISOString()

const dealInvoices: InvoicesByDealId = {
  1: [
    {
      id: 'mock-invoice-1-1',
      dealId: '1',
      createdTime: NOW_ISO,
      updatedTime: NOW_ISO,
      invoiceNumber: 'I#012526-JD-1-1',
      period: { startDate: '2026-01-25', endDate: '2027-01-24', label: '01/25/2026 – 01/24/2027' },
      status: 'Partially Paid',
      origin: 'auto',
      isCombinedRenewal: false,
      linkedShipmentIds: [],
      year: 1,
      items: [
        { productKey: 'ai_eye_sw_pro', productName: 'AI Eye Software — Pro (AI Harvest)', qty: 4, rateCents: 200000, discountPercent: 0, amountCents: 800000 },
        { productKey: 'ai_eye_hw_lease', productName: 'AI Eye Hardware (Lease)', qty: 8, rateCents: 60000, discountPercent: 0, amountCents: 480000 },
        { productKey: 'wireless_bridge', productName: 'Wireless Bridge', qty: 2, rateCents: 8000, discountPercent: 0, amountCents: 16000, isOTP: true },
        { productKey: 'cellular_modem_verizon', productName: 'Cellular Modem — Verizon', qty: 1, rateCents: 7500, discountPercent: 0, amountCents: 7500, isOTP: true },
        { productKey: 'data_monthly_verizon', productName: 'Monthly Data — Verizon (12 mo)', qty: 12, rateCents: 7500, discountPercent: 0, amountCents: 90000 },
        { productKey: 'intl_shipping', productName: 'International Shipping (DHL / FedEx)', qty: 1, rateCents: 110000, discountPercent: 0, amountCents: 110000, isOTP: true },
      ],
      subtotalCents: 1503500,
      totalCents: 1503500,
      paidAmountCents: 1000000,
      outstandingCents: 503500,
      paidInfo: 'Partial payment received 02/15/2026 — $10,000 of $15,035',
      paidDate: '02/15/2026',
      sentAt: null,
    },
    {
      id: 'mock-invoice-1-2',
      dealId: '1',
      createdTime: NOW_ISO,
      updatedTime: NOW_ISO,
      invoiceNumber: 'I#012527-JD-1-2',
      period: { startDate: '2027-01-25', endDate: '2028-01-24', label: '01/25/2027 – 01/24/2028' },
      status: 'Unpaid',
      origin: 'auto',
      isCombinedRenewal: false,
      linkedShipmentIds: [],
      year: 2,
      prevBalanceOverrideCents: 503500,
      items: [
        { productKey: 'ai_eye_sw_pro', productName: 'AI Eye Software — Pro (AI Harvest)', qty: 4, rateCents: 200000, discountPercent: 0, amountCents: 800000 },
        { productKey: 'ai_eye_hw_lease', productName: 'AI Eye Hardware (Lease)', qty: 8, rateCents: 60000, discountPercent: 0, amountCents: 480000 },
        { productKey: 'data_monthly_verizon', productName: 'Monthly Data — Verizon (12 mo)', qty: 12, rateCents: 7500, discountPercent: 0, amountCents: 90000 },
      ],
      subtotalCents: 1370000,
      totalCents: 1873500,
      paidAmountCents: 0,
      outstandingCents: 1873500,
      paidDate: null,
      sentAt: null,
    },
    {
      id: 'mock-invoice-1-3',
      dealId: '1',
      createdTime: NOW_ISO,
      updatedTime: NOW_ISO,
      invoiceNumber: 'I#012528-JD-1-3',
      period: { startDate: '2028-01-25', endDate: '2029-01-24', label: '01/25/2028 – 01/24/2029' },
      status: 'Paid',
      origin: 'auto',
      isCombinedRenewal: false,
      linkedShipmentIds: [],
      year: 3,
      items: [
        { productKey: 'ai_eye_sw_pro', productName: 'AI Eye Software — Pro (AI Harvest)', qty: 4, rateCents: 200000, discountPercent: 0, amountCents: 800000 },
        { productKey: 'ai_eye_hw_lease', productName: 'AI Eye Hardware (Lease, final year)', qty: 8, rateCents: 60000, discountPercent: 0, amountCents: 480000 },
        { productKey: 'data_monthly_verizon', productName: 'Monthly Data — Verizon (12 mo)', qty: 12, rateCents: 7500, discountPercent: 0, amountCents: 90000 },
      ],
      subtotalCents: 1370000,
      totalCents: 1370000,
      paidAmountCents: 1370000,
      outstandingCents: 0,
      paidInfo: 'Paid in full 02/05/2028 — $13,700',
      paidDate: '02/05/2028',
      sentAt: null,
    },
    {
      id: 'mock-invoice-1-4',
      dealId: '1',
      createdTime: NOW_ISO,
      updatedTime: NOW_ISO,
      invoiceNumber: 'I#060129-JD-1-4',
      period: { startDate: '2029-06-01', endDate: '2029-06-01', label: '06/01/2029 — One-time purchase' },
      status: 'Unpaid',
      origin: 'manual',
      addonType: 'onetime',
      isCombinedRenewal: false,
      linkedShipmentIds: [],
      year: 4,
      items: [
        { productKey: 'yw_nh3', productName: 'Y-Series Wired — NH3 only', qty: 4, rateCents: 78000, discountPercent: 0, amountCents: 312000 },
        { productKey: 'install_3rdparty', productName: 'Installation — 3rd party equipment support', qty: 1, rateCents: 50000, discountPercent: 0, amountCents: 50000 },
        { productName: 'Shipping (FedEx)', qty: 1, rateCents: 16000, discountPercent: 0, amountCents: 16000 },
      ],
      subtotalCents: 378000,
      totalCents: 378000,
      paidAmountCents: 0,
      outstandingCents: 378000,
      paidDate: null,
      sentAt: null,
      note: 'One-time hardware purchase — independent of the annual subscription cycle.',
    },
    {
      id: 'mock-invoice-1-5',
      dealId: '1',
      createdTime: NOW_ISO,
      updatedTime: NOW_ISO,
      invoiceNumber: 'I#090129-JD-1-5',
      period: { startDate: '2029-09-01', endDate: '2030-01-24', label: 'Pro-rated — 09/01/2029 to 01/24/2030' },
      status: 'Unpaid',
      origin: 'manual',
      addonType: 'prorated',
      isCombinedRenewal: false,
      linkedShipmentIds: [],
      year: 4,
      items: [
        { productKey: 'ai_eye_sw_pro', productName: 'AI Eye Software — Pro (AI Harvest) — extra house', qty: 1, rateCents: 83300, discountPercent: 0, amountCents: 83300 },
        { productKey: 'ai_eye_hw_lease', productName: 'AI Eye Hardware (Lease) — extra units', qty: 2, rateCents: 25000, discountPercent: 0, amountCents: 50000 },
        { productKey: 'wireless_bridge', productName: 'Wireless Bridge', qty: 1, rateCents: 8000, discountPercent: 0, amountCents: 8000 },
        { productKey: 'data_monthly_verizon', productName: 'Monthly Data — Verizon (5 mo)', qty: 5, rateCents: 7500, discountPercent: 0, amountCents: 37500 },
        { productKey: 'install_3rdparty', productName: 'Installation — 3rd party equipment support', qty: 1, rateCents: 41200, discountPercent: 0, amountCents: 41200 },
      ],
      subtotalCents: 220000,
      totalCents: 220000,
      paidAmountCents: 0,
      outstandingCents: 220000,
      paidDate: null,
      sentAt: null,
      note: 'Pro-rated add-on — new house added mid-subscription, billed for the remaining 5 months of Year 4.',
    },
    {
      id: 'mock-invoice-1-6',
      dealId: '1',
      createdTime: NOW_ISO,
      updatedTime: NOW_ISO,
      invoiceNumber: 'I#012529-JD-1-6',
      period: { startDate: '2029-01-25', endDate: '2030-01-24', label: '01/25/2029 – 01/24/2030' },
      status: 'Scheduled',
      origin: 'auto',
      isCombinedRenewal: false,
      linkedShipmentIds: [],
      year: 4,
      items: [
        { productKey: 'ai_eye_sw_pro', productName: 'AI Eye Software — Pro (AI Harvest)', qty: 4, rateCents: 200000, discountPercent: 0, amountCents: 800000 },
        { productKey: 'ai_eye_hw_lease', productName: 'AI Eye Hardware (Lease)', qty: 8, rateCents: 60000, discountPercent: 0, amountCents: 480000 },
        { productKey: 'data_monthly_verizon', productName: 'Monthly Data — Verizon (12 mo)', qty: 12, rateCents: 7500, discountPercent: 0, amountCents: 90000 },
      ],
      subtotalCents: 1370000,
      totalCents: 1370000,
      paidAmountCents: 0,
      outstandingCents: 1370000,
      paidDate: null,
      sentAt: null,
    },
    {
      id: 'mock-invoice-1-7',
      dealId: '1',
      createdTime: NOW_ISO,
      updatedTime: NOW_ISO,
      invoiceNumber: 'I#020126-JD-1-7',
      period: { startDate: '2026-02-01', endDate: '2026-02-01', label: '02/01/2026 — Add-on' },
      status: 'Draft',
      origin: 'manual',
      addonType: 'onetime',
      isCombinedRenewal: false,
      linkedShipmentIds: [],
      year: 1,
      items: [
        { productKey: 'wireless_bridge', productName: 'Wireless Bridge — extra unit', qty: 2, rateCents: 8000, discountPercent: 0, amountCents: 16000 },
        { productKey: 'install_3rdparty', productName: 'Installation — 3rd party equipment support', qty: 1, rateCents: 50000, discountPercent: 0, amountCents: 50000 },
        { productName: 'Shipping (FedEx)', qty: 1, rateCents: 8000, discountPercent: 0, amountCents: 8000 },
        { productKey: 'ai_eye_sw_pro', productName: 'AI Eye Software — Pro (extra house, pro-rated)', qty: 1, rateCents: 90000, discountPercent: 0, amountCents: 90000 },
      ],
      subtotalCents: 164000,
      totalCents: 164000,
      paidAmountCents: 0,
      outstandingCents: 164000,
      paidDate: null,
      sentAt: null,
      draftEmail: {
        to: 'ap@waynesanderson.com',
        cc: [],
        subject: 'Add-on invoice — Wireless Bridge & install',
        message: 'Hi team — attaching the add-on invoice for the extra house install. Let me know if you need a revised PO.',
      },
      note: 'Draft saved by JD Chang — pending review before send.',
    },
  ],
  2: [
    {
      id: 'mock-invoice-2-1',
      dealId: '2',
      createdTime: NOW_ISO,
      updatedTime: NOW_ISO,
      invoiceNumber: 'I#031526-AR-2-1',
      period: { startDate: '2026-03-15', endDate: '2026-06-14', label: '03/15/2026 – 06/14/2026' },
      status: 'Paid',
      origin: 'auto',
      isCombinedRenewal: false,
      linkedShipmentIds: [],
      items: [
        { productName: 'AI Camera (Lease)', qty: 12, rateCents: 25000, discountPercent: 0, amountCents: 300000 },
        { productName: 'AI Eye (SW)', qty: 4, rateCents: 30000, discountPercent: 0, amountCents: 120000 },
      ],
      subtotalCents: 420000,
      totalCents: 420000,
      paidAmountCents: 420000,
      outstandingCents: 0,
      paidInfo: 'Paid on 03/20/2026 — $4,200',
      paidDate: null,
      sentAt: null,
    },
  ],
  3: [
    {
      id: 'mock-invoice-3-1',
      dealId: '3',
      createdTime: NOW_ISO,
      updatedTime: NOW_ISO,
      invoiceNumber: 'I#020126-MT-3-1',
      period: { startDate: '2026-02-01', endDate: '2026-02-28', label: '02/01/2026 – 02/28/2026' },
      status: 'Paid',
      origin: 'auto',
      isCombinedRenewal: false,
      linkedShipmentIds: [],
      items: [
        { productName: 'AI Eye (SW)', qty: 2, rateCents: 25000, discountPercent: 0, amountCents: 50000 },
      ],
      subtotalCents: 50000,
      totalCents: 50000,
      paidAmountCents: 50000,
      outstandingCents: 0,
      paidInfo: 'Paid on 02/05/2026 — $500',
      paidDate: null,
      sentAt: null,
    },
    {
      id: 'mock-invoice-3-2',
      dealId: '3',
      createdTime: NOW_ISO,
      updatedTime: NOW_ISO,
      invoiceNumber: 'I#020126-MT-3-2',
      period: { startDate: '2026-03-01', endDate: '2026-03-31', label: '03/01/2026 – 03/31/2026' },
      status: 'Paid',
      origin: 'auto',
      isCombinedRenewal: false,
      linkedShipmentIds: [],
      items: [
        { productName: 'AI Eye (SW)', qty: 2, rateCents: 25000, discountPercent: 0, amountCents: 50000 },
      ],
      subtotalCents: 50000,
      totalCents: 50000,
      paidAmountCents: 50000,
      outstandingCents: 0,
      paidInfo: 'Paid on 03/05/2026 — $500',
      paidDate: null,
      sentAt: null,
    },
  ],
  4: [
    {
      id: 'mock-invoice-4-1',
      dealId: '4',
      createdTime: NOW_ISO,
      updatedTime: NOW_ISO,
      invoiceNumber: 'I#041026-JD-4-1',
      period: { startDate: '2026-04-10', endDate: '2027-04-09', label: '04/10/2026 – 04/09/2027' },
      status: 'Unpaid',
      origin: 'auto',
      isCombinedRenewal: false,
      linkedShipmentIds: [],
      items: [
        { productName: 'Y-Series (Lease)', qty: 4, rateCents: 150000, discountPercent: 0, amountCents: 600000 },
        { productName: 'Sensor Kit (OTP)', qty: 4, rateCents: 60000, discountPercent: 0, amountCents: 240000, isOTP: true },
      ],
      subtotalCents: 840000,
      totalCents: 840000,
      paidAmountCents: 0,
      outstandingCents: 840000,
      paidDate: null,
      sentAt: null,
    },
  ],
}

const dealShipments: ShipmentsByDealId = {
  1: [
    {
      id: '1',
      dealId: '1',
      createdTime: NOW_ISO,
      updatedTime: NOW_ISO,
      label: 'Shipment #1',
      carrier: '黑貓 (T-Cat)',
      trackingNumber: 'TC20260110-55231',
      linkedInvoiceNumber: 'I#012526-JD-1-1',
      devices: ['10012', '10013', '10014', '10015', '10016', '10017', '10018', '10019'],
      legs: [
        { type: 'warehouse', status: 'delivered', statusDate: '01/12' },
        { type: 'customer', carrier: 'FedEx', trackingNumber: '794644790148', status: 'delivered', statusDate: '01/22' },
      ],
    },
  ],
  4: [
    {
      id: '1',
      dealId: '4',
      createdTime: NOW_ISO,
      updatedTime: NOW_ISO,
      label: 'Shipment #1',
      carrier: '黑貓 (T-Cat)',
      trackingNumber: 'TC20260405-11100',
      linkedInvoiceNumber: 'I#041026-JD-4-1',
      devices: ['Y-301', 'Y-302', 'SK-01', 'SK-02'],
      legs: [
        { type: 'warehouse', status: 'delivered', statusDate: '04/07' },
        { type: 'customer', carrier: 'FedEx', trackingNumber: '794644920001', status: 'delivered', statusDate: '04/12' },
      ],
    },
    {
      id: '2',
      dealId: '4',
      createdTime: NOW_ISO,
      updatedTime: NOW_ISO,
      label: 'Shipment #2',
      carrier: '黑貓 (T-Cat)',
      trackingNumber: 'TC20260420-22210',
      linkedInvoiceNumber: 'I#041026-JD-4-1',
      devices: ['Y-303', 'Y-304', 'SK-03', 'SK-04'],
      legs: [
        { type: 'warehouse', status: 'delivered', statusDate: '04/22' },
        { type: 'customer', status: 'pending', statusDate: null },
      ],
    },
  ],
}

export const initialDealInvoices: InvoicesByDealId = dealInvoices
export const initialDealShipments: ShipmentsByDealId = dealShipments

export const initialDeals: Deal[] = [
  {
    id: '1',
    orderNumber: 'D#022526-JD-1',
    quoteNumber: 'Q-2025-0042',
    companyId: 'Simmons Foods',
    businessId: 'Broiler',
    salesRepUserId: 'mock_user_jd',
    salesRepInitials: 'JD',
    customerName: 'Wayne Sanderson',
    customerEmail: 'ap@waynesanderson.com',
    billingCadence: 'Annual',
    pricingRegion: 1,
    pricingTerm: 1,
    activationDate: '2026-01-25',
    expirationDate: '2030-01-24',
    lineItems: [
      { type: 'sw', label: 'SW', productKey: 'ai_eye_sw_pro', productName: 'AI Eye Software — Pro (AI Harvest)', qty: 4, assignments: { houses: [] } },
      { type: 'hw', label: 'Lease', productKey: 'ai_eye_hw_lease', productName: 'AI Eye Hardware (Lease)', qty: 8, assignments: { devices: [] } },
      { type: 'otp', label: 'OTP', productKey: 'wireless_bridge', productName: 'Wireless Bridge', qty: 2, assignments: { devices: [] } },
      { type: 'otp', label: 'OTP', productKey: 'data_monthly_verizon', productName: 'Monthly Data — Verizon', qty: 1, assignments: { devices: [] } },
    ],
    ccExecOn: true,
    ccExecs: ['jd.chang@calyx.ai', 'admin@calyx.ai'],
    lifecycle: { status: 'active', canceledMode: null, canceledAt: null },
    archivedAt: null,
    nextInvoiceSeq: (dealInvoices[1]?.length ?? 0) + 1,
    invoiceSummary: summaryFor(dealInvoices[1] ?? []),
    shipmentProgress: shipmentProgressFor(dealShipments[1] ?? []),
    createdTime: NOW_ISO,
    updatedTime: NOW_ISO,
    memo: 'Annual billing for Wayne Sanderson broiler operation. HW ownership transfers after Y3.',
  },
  {
    id: '2',
    orderNumber: 'D#031526-AR-2',
    quoteNumber: 'Q-2025-0078',
    companyId: 'Tyson',
    businessId: 'Broiler',
    salesRepUserId: 'mock_user_ar',
    salesRepInitials: 'AR',
    customerName: 'Tyson Foods AR',
    customerEmail: 'billing@tyson.com',
    billingCadence: 'Quarterly',
    pricingRegion: 1,
    pricingTerm: 1,
    activationDate: '2026-03-15',
    expirationDate: '2027-03-14',
    lineItems: [
      { type: 'hw', label: 'Lease', productKey: 'ai_eye_hw_lease', productName: 'AI Eye', qty: 12, assignments: { devices: [] } },
      { type: 'sw', label: 'SW', productKey: 'ai_eye_sw', productName: 'AI Eye', qty: 4, assignments: { houses: [] } },
    ],
    ccExecOn: true,
    ccExecs: ['ar.smith@calyx.ai'],
    lifecycle: { status: 'active', canceledMode: null, canceledAt: null },
    archivedAt: null,
    nextInvoiceSeq: (dealInvoices[2]?.length ?? 0) + 1,
    invoiceSummary: summaryFor(dealInvoices[2] ?? []),
    shipmentProgress: shipmentProgressFor(dealShipments[2] ?? []),
    createdTime: NOW_ISO,
    updatedTime: NOW_ISO,
  },
  {
    id: '3',
    orderNumber: 'D#020126-MT-3',
    quoteNumber: 'Q-2025-0019',
    companyId: '新長發牧場',
    businessId: 'Broiler',
    salesRepUserId: 'mock_user_mt',
    salesRepInitials: 'MT',
    customerName: '新長發牧場',
    customerEmail: 'billing@xinchangfa.tw',
    billingCadence: 'Monthly',
    pricingRegion: 1,
    pricingTerm: 1,
    activationDate: '2026-02-01',
    expirationDate: '2027-01-31',
    lineItems: [
      { type: 'sw', label: 'SW', productKey: 'ai_eye_sw', productName: 'AI Eye', qty: 2, assignments: { houses: [] } },
    ],
    ccExecOn: true,
    ccExecs: ['mt.lin@calyx.ai'],
    lifecycle: { status: 'active', canceledMode: null, canceledAt: null },
    archivedAt: null,
    nextInvoiceSeq: (dealInvoices[3]?.length ?? 0) + 1,
    invoiceSummary: summaryFor(dealInvoices[3] ?? []),
    shipmentProgress: shipmentProgressFor(dealShipments[3] ?? []),
    createdTime: NOW_ISO,
    updatedTime: NOW_ISO,
  },
  {
    id: '4',
    orderNumber: 'D#041026-JD-4',
    quoteNumber: 'Q-2026-0103',
    companyId: 'Pavilion Farms',
    businessId: 'Layer',
    salesRepUserId: 'mock_user_jd',
    salesRepInitials: 'JD',
    customerName: 'Pavilion Farms',
    customerEmail: 'billing@pavilionfarms.com',
    billingCadence: 'Annual',
    pricingRegion: 1,
    pricingTerm: 1,
    activationDate: '2026-04-10',
    expirationDate: '2027-04-09',
    lineItems: [
      { type: 'hw', label: 'Lease', productKey: 'y_series', productName: 'Y-Series', qty: 4, assignments: { devices: ['Y-301', 'Y-302', 'Y-303', 'Y-304'] } },
      { type: 'otp', label: 'OTP', productKey: 'sensor_kit', productName: 'Sensor Kit', qty: 4, assignments: { devices: ['SK-01', 'SK-02', 'SK-03', 'SK-04'] } },
    ],
    ccExecOn: true,
    ccExecs: ['jd.chang@calyx.ai'],
    lifecycle: { status: 'active', canceledMode: null, canceledAt: null },
    archivedAt: null,
    nextInvoiceSeq: (dealInvoices[4]?.length ?? 0) + 1,
    invoiceSummary: summaryFor(dealInvoices[4] ?? []),
    shipmentProgress: shipmentProgressFor(dealShipments[4] ?? []),
    createdTime: NOW_ISO,
    updatedTime: NOW_ISO,
  },
]
